import { Howl } from "howler";
import { useEffect, useRef, useState } from "react";

export interface PreloadedAudio {
  preload: (url: string) => void;
  play: (onEnd?: () => void) => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  cleanup: () => void;
  duration: number;
  isLoaded: boolean;
}

export default function usePreloadedAudio(): PreloadedAudio {
  const onEndRef = useRef<(() => void) | null>(null);
  const howlRef = useRef<Howl | null>(null);
  const howlUrl = useRef<string>("");
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  function preload(url: string) {
    // Ignore falsy urls
    if (!url) {
      return;
    }

    // Do not bother loading if the new URL matches our current preloaded
    // content.
    if (url === howlUrl.current && howlRef.current?.state() === "loaded") {
      return;
    }
    
    // Cleanup any previously-loaded data.
    cleanup();
    setIsLoaded(false);
    
    // Preload and ready the new URL
    howlUrl.current = url;
    howlRef.current = new Howl({
      src: [url],
      format: ["mp3"],
      preload: true,
      html5: true,
      onload: () => {
        setDuration(howlRef.current?.duration() ?? 0);
        setIsLoaded(true);
      },
      onloaderror: (_id, err) => {
        console.error("Howl load error:", err);
      },
    });
  }

  function play(onEnd?: () => void) {
    onEndRef.current = onEnd ?? null;

    const howl = howlRef.current;
    if (howl == null) {
      console.log("No preloaded audio detected: " + howlUrl.current);
      return;
    }

    if (isPaused === false) {
      howl.once("end", () => {
        onEndRef.current?.();
      });
    }

    setIsPaused(false);

    function playAudio() {
      howl?.play();
      setDuration(howl?.duration() ?? 0);
      setIsLoaded(true);
    }

    if (howl.state() === "loaded") {
      playAudio();
    } else {
      howl.once("load", () => playAudio());
    }
  }

  function stop() {
    setIsPaused(false);
    onEndRef.current?.();
    onEndRef.current = null;
    howlRef.current?.stop();
  }

  function pause() {
    if (isPaused === true || howlRef.current == null) {
      return;
    }

    setIsPaused(true);
    howlRef.current?.pause();
  }

  function seek(time: number) {
    if (howlRef.current == null) {
      return;
    }

    howlRef.current?.seek(time);
  }

  function cleanup() {
    onEndRef.current = null;
    if (howlRef.current) {
      howlRef.current?.stop();
      howlRef.current?.unload();
      howlRef.current?.off();
      howlRef.current = null;
      howlUrl.current = "";
    }
  }

  useEffect(() => {
    const handleRefresh = () => {
      cleanup();
    };
    window.addEventListener("beforeunload", handleRefresh);
    return () => {
      window.removeEventListener("beforeunload", handleRefresh);
      cleanup();
    };
  }, []);

  return { preload, play, pause, stop, seek, cleanup, duration, isLoaded };
}
